import {
  convertQty,
  type ReversibleStockMovementV1,
  type StockConversionSource,
  type StockSourceLineV1,
} from '@trackeat/shared';

export interface ProductionFinishedFood {
  id: string;
  default_uom: string;
  recipe_id?: string | null;
  archived_at?: string | null;
}

export type ProductionFinishedFoodPlan =
  | { kind: 'not_applicable' }
  | { kind: 'existing'; food: ProductionFinishedFood }
  | { kind: 'create'; foodId: string };

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizedUom(value: string): string {
  return value.trim().toLowerCase();
}

function normalizedCurrency(value: string): string {
  const currency = value.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new Error('Valuta del movimento non valida');
  }
  return currency;
}

async function deterministicUuid(namespace: string): Promise<string> {
  const encoded = new TextEncoder().encode(namespace);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', encoded));
  const bytes = digest.slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function productionFinishedFoodIdFor(
  tenantId: string,
  recipeId: string,
): Promise<string> {
  if (!UUID_PATTERN.test(tenantId) || !UUID_PATTERN.test(recipeId)) {
    throw new Error('Identità tenant o ricetta non valida');
  }
  return deterministicUuid(
    `production_finished_food|${tenantId.toLowerCase()}|${recipeId.toLowerCase()}`,
  );
}

/**
 * Collective-catering finished dishes intentionally remain outside stock
 * (T-425). In every other tenant a manual production must resolve one recipe
 * to one active finished food; a free-text name is never guessed as a food.
 */
export async function planProductionFinishedFood(input: {
  tenantId: string;
  recipeId: string | null;
  collettivaEnabled: boolean;
  foods: readonly ProductionFinishedFood[];
}): Promise<ProductionFinishedFoodPlan> {
  if (input.collettivaEnabled) return { kind: 'not_applicable' };
  if (!input.recipeId) {
    throw new Error(
      'Seleziona una ricetta: senza un alimento associato l’output non può essere caricato in magazzino',
    );
  }

  const activeMatches = input.foods.filter((food) => (
    food.recipe_id === input.recipeId
    && !food.archived_at
  ));
  if (activeMatches.length > 1) {
    throw new Error(
      'La ricetta ha più prodotti finiti attivi: correggi l’anagrafica prima di registrare la lavorazione',
    );
  }
  if (activeMatches[0]) {
    return { kind: 'existing', food: activeMatches[0] };
  }

  const foodId = await productionFinishedFoodIdFor(input.tenantId, input.recipeId);
  const idCollision = input.foods.find((food) => food.id === foodId);
  if (idCollision) {
    throw new Error(
      'Il prodotto finito deterministico è già archiviato o associato a un’altra ricetta: ripristina l’anagrafica prima di continuare',
    );
  }
  return { kind: 'create', foodId };
}

export function createProductionStockLine(input: {
  foodId: string;
  batchLot: string | null;
  expirationDate: string | null;
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
  qty: number;
  sourceUom: string;
  stockUom: string;
  unitCost: number | null;
  sourceLineTotal?: number | null;
  currency: string;
  lineRef: string;
  operatorName: string | null;
  lotAction: 'updated_active' | 'recorded_only';
}): StockSourceLineV1 {
  if (!Number.isFinite(input.qty) || input.qty <= 0) {
    throw new Error('Quantità del movimento di produzione non valida');
  }
  const sourceUom = input.sourceUom.trim();
  const stockUom = input.stockUom.trim();
  const factor = convertQty(1, sourceUom, stockUom);
  if (factor == null || factor <= 0) {
    throw new Error(`UdM incompatibili per la produzione: ${sourceUom} → ${stockUom}`);
  }

  const explicitTotal = input.sourceLineTotal;
  if (
    explicitTotal != null
    && (!Number.isFinite(explicitTotal) || explicitTotal < 0)
  ) {
    throw new Error('Valore totale della produzione non valido');
  }
  const sourceUnitPrice = explicitTotal !== undefined
    ? explicitTotal == null ? null : explicitTotal / input.qty
    : input.unitCost == null ? null : input.unitCost * factor;

  return {
    food_id: input.foodId,
    batch_lot: input.batchLot,
    expiration_date: input.expirationDate,
    supplier_id: input.supplierId,
    document_id: input.documentId,
    storage_unit_id: input.storageUnitId,
    line_ref: input.lineRef,
    lot_action: input.lotAction,
    operator_name: input.operatorName,
    source_qty: input.qty,
    source_uom: sourceUom,
    source_unit_price: sourceUnitPrice,
    source_line_total: explicitTotal !== undefined
      ? explicitTotal
      : sourceUnitPrice == null ? null : sourceUnitPrice * input.qty,
    currency: normalizedCurrency(input.currency),
    stock_uom: stockUom,
    conversion: {
      factor,
      source: normalizedUom(sourceUom) === normalizedUom(stockUom) ? 'identity' : 'metric',
    },
  };
}

export function carriedProductionOutputValue(
  inputs: readonly Pick<StockSourceLineV1, 'source_line_total' | 'currency'>[],
): { lineTotal: number | null; currency: string } {
  if (
    inputs.length === 0
    || inputs.some((line) => line.source_line_total == null)
  ) {
    return { lineTotal: null, currency: 'EUR' };
  }
  const currencies = new Set(inputs.map((line) => line.currency));
  if (currencies.size !== 1) {
    return { lineTotal: null, currency: 'EUR' };
  }
  return {
    lineTotal: inputs.reduce(
      (total, line) => total + (line.source_line_total ?? 0),
      0,
    ),
    currency: inputs[0]?.currency ?? 'EUR',
  };
}

export function productionBatchLot(productionId: string): string {
  return `PROD-${productionId}`;
}

export interface ProductionReceiptMetadata {
  stock_command_id?: string;
  stock_command_event_count?: number;
}

export function productionReceiptCoordinates(
  inputs: readonly ProductionReceiptMetadata[],
): { commandId: string; expectedEventCount: number } {
  const first = inputs[0];
  const commandId = first?.stock_command_id;
  const expectedEventCount = first?.stock_command_event_count;
  if (
    typeof commandId !== 'string'
    || !UUID_PATTERN.test(commandId)
    || typeof expectedEventCount !== 'number'
    || !Number.isInteger(expectedEventCount)
    || expectedEventCount <= 0
    || inputs.some((input) => (
      input.stock_command_id !== commandId
      || input.stock_command_event_count !== expectedEventCount
    ))
  ) {
    throw new Error(
      'Lavorazione legacy senza ricevuta StockCommand: annullamento bloccato per evitare uno storno sintetico',
    );
  }
  return { commandId, expectedEventCount };
}

export interface ProductionCommandEvent {
  id?: string | null;
  server_id?: string | null;
  business_id?: string | null;
  kind: string;
  payload: Record<string, unknown>;
}

const STOCK_CONVERSION_SOURCES = new Set<StockConversionSource>([
  'identity',
  'metric',
  'document_pack',
  'document_weight',
  'supplier_alias',
  'user_confirmed',
  'carried',
]);

function eventBusinessId(event: ProductionCommandEvent): string | null {
  const payloadBusinessId = event.payload.business_id;
  const topLevel = event.business_id ?? null;
  const nested = typeof payloadBusinessId === 'string' ? payloadBusinessId : null;
  if (topLevel && nested && topLevel !== nested) return null;
  return topLevel ?? nested;
}

function nullableUuid(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  if (value == null) return null;
  if (typeof value !== 'string' || !UUID_PATTERN.test(value)) {
    throw new Error(`Movimento originale non valido: ${key}`);
  }
  return value;
}

function positiveNumber(payload: Record<string, unknown>, key: string): number {
  const value = payload[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`Movimento originale incompleto: ${key}`);
  }
  return value;
}

function nullableNonNegativeNumber(
  payload: Record<string, unknown>,
  key: string,
): number | null {
  const value = payload[key];
  if (value == null) return null;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Movimento originale non valido: ${key}`);
  }
  return value;
}

function reverseMovementFromProductionEvent(
  event: ProductionCommandEvent,
): ReversibleStockMovementV1 {
  const payload = event.payload;
  const originalSeq = payload.command_seq;
  const foodId = payload.food_id;
  const sourceUom = payload.source_uom;
  const stockUom = payload.stock_uom;
  const sourceCurrency = payload.source_currency;
  const conversionFactor = payload.conversion_factor;
  const conversionSource = payload.conversion_source;
  const businessId = eventBusinessId(event);
  if (
    (event.kind !== 'stock_in' && event.kind !== 'stock_out')
    || typeof originalSeq !== 'number'
    || !Number.isInteger(originalSeq)
    || originalSeq <= 0
    || typeof foodId !== 'string'
    || !UUID_PATTERN.test(foodId)
    || typeof sourceUom !== 'string'
    || sourceUom.trim() === ''
    || typeof stockUom !== 'string'
    || stockUom.trim() === ''
    || typeof sourceCurrency !== 'string'
    || !/^[A-Z]{3}$/.test(sourceCurrency)
    || typeof conversionFactor !== 'number'
    || !Number.isFinite(conversionFactor)
    || conversionFactor <= 0
    || typeof conversionSource !== 'string'
    || !STOCK_CONVERSION_SOURCES.has(conversionSource as StockConversionSource)
    || businessId == null
    || !UUID_PATTERN.test(businessId)
  ) {
    throw new Error('Il comando originale non contiene dati sufficienti per uno storno sicuro');
  }
  if (typeof event.id !== 'string' || !UUID_PATTERN.test(event.id)) {
    throw new Error('Identificativo locale del movimento originale non valido');
  }
  if (typeof event.server_id !== 'string' || !UUID_PATTERN.test(event.server_id)) {
    throw new Error('Sincronizza i movimenti prima di annullare la lavorazione');
  }
  const lineRef = payload.source_line_ref;
  if (lineRef != null && typeof lineRef !== 'string') {
    throw new Error('Riferimento del movimento originale non valido');
  }
  const referenceId = payload.conversion_reference_id;
  if (referenceId != null && typeof referenceId !== 'string') {
    throw new Error('Riferimento di conversione originale non valido');
  }
  const expirationDate = payload.expiration_date;
  if (expirationDate != null && typeof expirationDate !== 'string') {
    throw new Error('Scadenza del movimento originale non valida');
  }
  const batchLot = payload.batch_lot;
  if (batchLot != null && typeof batchLot !== 'string') {
    throw new Error('Lotto del movimento originale non valido');
  }

  return {
    food_id: foodId,
    batch_lot: batchLot ?? null,
    expiration_date: expirationDate ?? null,
    supplier_id: nullableUuid(payload, 'supplier_id'),
    document_id: nullableUuid(payload, 'document_id'),
    storage_unit_id: nullableUuid(payload, 'storage_unit_id'),
    line_ref: lineRef ?? null,
    original_event_id: event.server_id,
    original_event_client_uuid: event.id,
    original_seq: originalSeq,
    kind: event.kind,
    business_id: businessId,
    source_qty: positiveNumber(payload, 'source_qty'),
    source_uom: sourceUom,
    source_unit_price: nullableNonNegativeNumber(payload, 'source_unit_price'),
    source_line_total: nullableNonNegativeNumber(payload, 'source_line_total'),
    currency: sourceCurrency,
    stock_qty: positiveNumber(payload, 'stock_qty'),
    stock_uom: stockUom,
    unit_cost: nullableNonNegativeNumber(payload, 'stock_unit_cost'),
    conversion: {
      factor: conversionFactor,
      source: conversionSource as StockConversionSource,
      ...(referenceId == null ? {} : { reference_id: referenceId }),
    },
  };
}

/**
 * Rebuilds a reverse exclusively from the exact, server-receipted command
 * events. No quantity, locator or valuation is inferred from the current UI.
 */
export function productionReverseMovements(input: {
  events: readonly ProductionCommandEvent[];
  commandId: string;
  expectedEventCount: number;
  productionId: string;
  businessId: string;
}): ReversibleStockMovementV1[] {
  if (
    !UUID_PATTERN.test(input.commandId)
    || !UUID_PATTERN.test(input.productionId)
    || !UUID_PATTERN.test(input.businessId)
    || !Number.isInteger(input.expectedEventCount)
    || input.expectedEventCount <= 0
  ) {
    throw new Error('Metadati StockCommand della lavorazione non validi');
  }
  const priorReverse = input.events.some((event) => (
    eventBusinessId(event) === input.businessId
    && event.payload.movement_class === 'reverse'
    && event.payload.reverses_command_id === input.commandId
  ));
  if (priorReverse) {
    throw new Error('La lavorazione risulta già annullata: sincronizza la pagina');
  }

  const originals = input.events
    .filter((event) => (
      eventBusinessId(event) === input.businessId
      && event.payload.movement_class === 'production'
      && event.payload.command_id === input.commandId
      && event.payload.source_production_id === input.productionId
    ))
    .sort((left, right) => (
      Number(left.payload.command_seq) - Number(right.payload.command_seq)
    ));
  if (originals.length !== input.expectedEventCount) {
    throw new Error(
      `Comando originale incompleto: trovati ${originals.length} movimenti su ${input.expectedEventCount}. Sincronizza prima di annullare.`,
    );
  }
  originals.forEach((event, index) => {
    if (event.payload.command_seq !== index + 1) {
      throw new Error('Comando originale incompleto: sequenza movimenti non continua');
    }
    const lineRef = event.payload.source_line_ref;
    if (
      typeof lineRef !== 'string'
      || !lineRef.startsWith(`production:${input.productionId}:`)
    ) {
      throw new Error('Comando originale non coerente con la lavorazione selezionata');
    }
  });
  return originals.map(reverseMovementFromProductionEvent);
}
